import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { tasksAPI } from "../../api/client";
import useAuth from "../../hooks/useAuth";
import { ColumnChart, Meter } from "./DashboardCharts";
import { apiError, formatDueDate, formatMinutes, initials } from "./taskUtils";
import { parseLocalDate } from "../../utils/dateUtils";
import "./Tasks.css";

function formatDay(key, long) {
  return parseLocalDate(key).toLocaleDateString(
    "en-IN",
    long
      ? { weekday: "short", day: "numeric", month: "short" }
      : { day: "numeric", month: "short" },
  );
}

function Stat({ label, value, sub, tone }) {
  return (
    <div className={`tk-stat ${tone ? `tk-stat-${tone}` : ""}`.trim()}>
      <span className="tk-stat-label">{label}</span>
      <span className="tk-stat-value">{value}</span>
      {sub && <span className="tk-stat-sub">{sub}</span>}
    </div>
  );
}

function WarningIcon() {
  return (
    <svg className="tk-status-icon" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 3l9.5 17h-19z" />
      <path d="M12 10v4M12 17.5v.01" className="tk-status-icon-mark" />
    </svg>
  );
}

// "Task Analytics" panel on the main Dashboard: company-wide task health,
// workload by person and project, and time logged on tasks.
export default function TeamTaskAnalytics() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    tasksAPI
      .teamOverview()
      .then((resp) => setData(resp.data))
      .catch((err) => setError(apiError(err, "Could not load task analytics")));
  }, []);

  if (error) return <p className="tk-error">{error}</p>;
  if (!data) return <p className="small-muted">Loading task analytics…</p>;

  const { totals } = data;
  if (totals.total === 0) {
    return (
      <p className="small-muted">
        No tasks yet. Task metrics appear here once your team starts using My
        Tasks.
      </p>
    );
  }

  const delta = totals.completedLast7 - totals.completedPrev7;
  const completionPct =
    totals.completionRate === null
      ? "—"
      : `${Math.round(totals.completionRate * 100)}%`;

  return (
    <div className="tk-dash tk-team">
      <section className="tk-team-kpis" aria-label="Task summary">
        <Stat
          label="Open tasks"
          value={totals.open}
          sub={`${totals.total} in total`}
        />
        <Stat
          label="Overdue"
          value={
            <>
              {totals.overdue > 0 && <WarningIcon />}
              {totals.overdue}
            </>
          }
          tone={totals.overdue > 0 ? "critical" : null}
          sub={totals.overdue > 0 ? "Past their due date" : "Nothing overdue"}
        />
        <Stat label="Due today" value={totals.dueToday} />
        <Stat label="Due in the next 7 days" value={totals.dueThisWeek} />
        <Stat
          label="Completed in the last 7 days"
          value={totals.completedLast7}
          sub={
            delta === 0 ? (
              "Same as previous 7 days"
            ) : (
              <span className={delta > 0 ? "tk-delta-up" : "tk-delta-down"}>
                {delta > 0 ? "▲" : "▼"} {Math.abs(delta)} vs previous 7 days
              </span>
            )
          }
        />
        <Stat
          label="Completion rate"
          value={completionPct}
          sub={
            <Meter value={totals.completionRate} label="Task completion rate" />
          }
        />
        <Stat
          label="Time logged on tasks"
          value={formatMinutes(totals.minutesLogged7)}
          sub={`Last 7 days · ${formatMinutes(totals.minutesLogged30)} in 30 days`}
        />
        <Stat
          label="Unassigned open tasks"
          value={totals.unassignedOpen}
          sub={`${totals.waitingOn} waiting on other tasks`}
        />
      </section>

      <div className="tk-dash-grid">
        <section className="tk-card tk-card-wide">
          <h3>Tasks completed per day</h3>
          <p className="tk-muted">Whole company, last 14 days</p>
          <ColumnChart
            data={data.completedByDay.map((d) => ({
              key: d.date,
              value: d.count,
            }))}
            valueLabel="completed"
            formatX={formatDay}
            ariaLabel="Tasks completed per day across the company over the last 14 days"
          />
        </section>

        <section className="tk-card tk-card-wide">
          <h3>Workload by person</h3>
          <p className="tk-muted">
            Open and overdue tasks assigned to each person; completed and time
            logged over the last 30 days
          </p>
          {data.byPerson.length === 0 ? (
            <p className="tk-muted">No assigned tasks yet.</p>
          ) : (
            <div className="tk-table-scroll">
              <table className="tk-viz-table tk-people-table">
                <thead>
                  <tr>
                    <th>Person</th>
                    <th>Open</th>
                    <th>Overdue</th>
                    <th>Completed (30d)</th>
                    <th>Time logged (30d)</th>
                  </tr>
                </thead>
                <tbody>
                  {data.byPerson.map((p) => (
                    <tr key={p.id}>
                      <td>
                        <span className="tk-assignee">
                          <span className="tk-avatar tk-avatar-sm">
                            {initials(p.name)}
                          </span>
                          {p.name}
                        </span>
                      </td>
                      <td>{p.open}</td>
                      <td className={p.overdue > 0 ? "tk-cell-critical" : ""}>
                        {p.overdue > 0 && <WarningIcon />}
                        {p.overdue}
                      </td>
                      <td>{p.completed30}</td>
                      <td>{p.minutes30 ? formatMinutes(p.minutes30) : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section className="tk-card">
          <h3>Tasks by project</h3>
          <p className="tk-muted">Open tasks and time logged (30d)</p>
          {data.byProject.length === 0 ? (
            <p className="tk-muted">No tasks are linked to projects yet.</p>
          ) : (
            <table className="tk-viz-table">
              <thead>
                <tr>
                  <th>Project</th>
                  <th>Open</th>
                  <th>Overdue</th>
                  <th>Time</th>
                </tr>
              </thead>
              <tbody>
                {data.byProject.map((p) => (
                  <tr key={p.id}>
                    <td>
                      <Link className="tk-table-link" to={`/projects/${p.id}`}>
                        {p.name}
                      </Link>
                    </td>
                    <td>{p.open}</td>
                    <td className={p.overdue > 0 ? "tk-cell-critical" : ""}>
                      {p.overdue}
                    </td>
                    <td>{p.minutes30 ? formatMinutes(p.minutes30) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        <section className="tk-card">
          <h3>
            {data.overdueTasks.length > 0 && <WarningIcon />}
            Most overdue
          </h3>
          {data.overdueTasks.length === 0 ? (
            <p className="tk-muted">Nothing overdue across the company.</p>
          ) : (
            <ul className="tk-mini-list">
              {data.overdueTasks.map((t) => {
                const body = (
                  <>
                    <span className="tk-mini-name">
                      {t.name}
                      <span className="tk-muted">
                        {" "}
                        · {t.assigneeName || "Unassigned"}
                      </span>
                    </span>
                    <span className="tk-due due-overdue">
                      {formatDueDate(t.dueDate)}
                    </span>
                  </>
                );
                // Admins can open any task; others may not have access to it.
                return (
                  <li key={t.id}>
                    {user?.isAdmin ? (
                      <Link className="tk-mini-link" to={`/tasks/${t.id}`}>
                        {body}
                      </Link>
                    ) : (
                      <div className="tk-mini-link tk-mini-static">{body}</div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}
